import React, { useEffect, useCallback, memo } from 'react';
import styled from 'styled-components';
import { NOTIFICATION_TYPES, NotificationType, TIMEOUTS } from '../../config/constants';

const NotificationContainer = styled.div<{ severity: NotificationType }>`
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 12px 20px;
  border-radius: 8px;
  background-color: ${({ severity }) => getBackgroundColor(severity)};
  color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  animation: slideIn 0.3s ease;

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const Message = styled.span`
  font-size: 14px;
`;

const CloseButton = styled.span`
  position: absolute;
  top: 12px;
  right: 12px;
  cursor: pointer;
`;

interface NotificationProps {
  message: string;
  type: NotificationType;
  onClose: () => void;
}

const getBackgroundColor = (type: NotificationType) => {
  switch (type) {
    case NOTIFICATION_TYPES.ERROR:
      return '#ff4444';
    case NOTIFICATION_TYPES.WARNING:
      return '#ffbb33';
    case NOTIFICATION_TYPES.INFO:
      return '#33b5e5';
    case NOTIFICATION_TYPES.SUCCESS:
      return '#00C851';
    default:
      return '#33b5e5';
  }
};

const Notification: React.FC<NotificationProps> = memo(({ message, type, onClose }) => {
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    const timer = setTimeout(() => {
      handleClose();
    }, TIMEOUTS.NOTIFICATION_DISPLAY);

    return () => {
      clearTimeout(timer);
    };
  }, [handleClose]);

  return (
    <NotificationContainer severity={type}>
      <Message>{message}</Message>
      <CloseButton onClick={handleClose}>&times;</CloseButton>
    </NotificationContainer>
  );
});

Notification.displayName = 'Notification';

export default Notification;

